import { Camera, Check, Clock, Home, KeyRound, MapPin, Users } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';

import { ClientPage } from '@/components/client/ClientPage';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { StatusBadge } from '@/components/ui/status-badge';
import { useClientJob } from '@/hooks/client-portal';
import { JOB_STATUS_TONES } from '@/hooks/jobs';
import { formatDay, formatDuration, formatInstant, formatWallTime } from '@/lib/dates';
import { ChangeRequestDialog } from '@/pages/client/ChangeRequestDialog';
import type { ChangeRequestKindValue } from '@shared/schemas/change-request';
import type { ClientJobDetail, ClientJobPhotoView } from '@shared/schemas/client-portal';

/*
 * /portal/cleans/:id — ROADMAP.md §5.2.
 *
 * Job number, schedule, duration, address, crew, property summary, the checklist
 * with per-task photo counts, and the gallery. What is **not** here is as
 * deliberate: no status-history timeline (it carries office names and internal
 * notes), no `client_note`, no rates, no satisfaction rating. The server never
 * sends them — see client-portal-service — so there is nothing here to forget to
 * hide.
 */

export function CleanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const job = useClientJob(id);

  if (job.error) {
    return (
      <ClientPage title="Clean" back={{ to: '/portal/cleans', label: 'Back to my cleans' }}>
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="We could not load this clean"
            hint="It may not exist, or something went wrong on our side. Ring the office on (435) 238-3286 if this keeps happening."
            onRetry={() => {
              void job.refetch();
            }}
          />
        </div>
      </ClientPage>
    );
  }

  if (job.isPending || !job.data) {
    return (
      <ClientPage title="Clean" back={{ to: '/portal/cleans', label: 'Back to my cleans' }}>
        <div aria-busy="true">
          <span className="sr-only">Loading this clean</span>
          <div aria-hidden="true" className="h-64 animate-pulse rounded-lg bg-ink-100" />
        </div>
      </ClientPage>
    );
  }

  const detail = job.data;

  return (
    <ClientPage
      title={detail.serviceTypeName}
      description={detail.jobNumber}
      back={{ to: '/portal/cleans', label: 'Back to my cleans' }}
      actions={<StatusBadge label={detail.statusLabel} tone={JOB_STATUS_TONES[detail.status]} />}
    >
      <ChangeRequestSection detail={detail} />

      {detail.cancelledReason ? (
        <p className="mb-4 rounded-sm border border-status-priority-text/30 bg-red-50 px-3 py-2.5 text-body text-status-priority-text">
          <span className="font-medium">This clean was cancelled.</span> {detail.cancelledReason}
        </p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="When and where">
          <dl className="space-y-3">
            <Row icon={Clock} label="Scheduled">
              {formatDay(detail.scheduledDate)} at {formatWallTime(detail.scheduledTime)}
              <span className="block text-meta text-ink-500">
                About {formatDuration(detail.estimatedDurationMinutes)}
              </span>
            </Row>

            <Row icon={MapPin} label="Address">
              {detail.propertyNickname ? (
                <span className="block font-medium">{detail.propertyNickname}</span>
              ) : null}
              {detail.addressSnapshot}
            </Row>

            <Row icon={Users} label="Your cleaner">
              {detail.crew.length > 0 ? (
                <ul>
                  {detail.crew.map((member) => (
                    <li key={member.name}>
                      {member.name}
                      <span className="text-meta text-ink-500"> · {member.roleLabel}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="italic text-ink-500">
                  Not assigned yet — we will confirm who is coming
                </span>
              )}
            </Row>

            {detail.startedAt ? (
              <Row icon={Check} label="Started">
                {formatInstant(detail.startedAt)}
              </Row>
            ) : null}
            {detail.completedAt ? (
              <Row icon={Check} label="Finished">
                {formatInstant(detail.completedAt)}
              </Row>
            ) : null}
          </dl>
        </Card>

        <Card title="The property">
          {detail.property ? (
            <dl className="space-y-3">
              <Row icon={Home} label="Details">
                {describeProperty(detail.property)}
                {detail.property.categoryName ? (
                  <span className="block text-meta text-ink-500">
                    Cleaned as: {detail.property.categoryName}
                  </span>
                ) : null}
              </Row>

              {detail.property.specialInstructions ? (
                <Row icon={MapPin} label="Your instructions">
                  {detail.property.specialInstructions}
                </Row>
              ) : null}

              {detail.property.accessNotes ? (
                <Row icon={KeyRound} label="Getting in">
                  {detail.property.accessNotes}
                </Row>
              ) : null}
            </dl>
          ) : (
            // The property was removed but `address_snapshot` still holds where
            // the clean happened — that is the snapshot doing its job.
            <p className="text-body italic text-ink-500">
              This address is no longer saved on your account. The clean still happened at{' '}
              {detail.addressSnapshot}.
            </p>
          )}

          {detail.specialInstructions ? (
            <p className="mt-4 rounded-sm border border-ink-300 bg-honey-50 px-3 py-2.5 text-meta text-bark-900">
              Notes for this visit: {detail.specialInstructions}
            </p>
          ) : null}
        </Card>
      </div>

      <Checklist detail={detail} />
      <Gallery photos={detail.photos} />
    </ClientPage>
  );
}

/**
 * Ask us to move it, or cancel it — ROADMAP.md §5.6, defect P19.
 *
 * The prototype has no way to do either, which is why the business gets phone
 * calls. Three states, and they are different things: nothing sent yet (two
 * buttons), one already waiting (what was asked, and that it is waiting), or one
 * answered (the office's note, which the client reads on an approval as well as
 * a decline — P16).
 */
function ChangeRequestSection({ detail }: { detail: ClientJobDetail }) {
  const [asking, setAsking] = useState<ChangeRequestKindValue | null>(null);
  const open = detail.openChangeRequest;

  if (open) {
    return (
      <div className="mb-4 rounded-lg border border-honey-500 bg-honey-50 p-4">
        <p className="text-label text-bark-900">
          {open.kind === 'cancellation'
            ? 'You have asked us to cancel this clean.'
            : `You have asked us to move this to ${open.requestedDate ? formatDay(open.requestedDate) : 'another day'}${open.requestedTime ? ` at ${formatWallTime(open.requestedTime)}` : ''}.`}
        </p>
        <p className="mt-1 text-body text-bark-900">
          We have not answered yet, so nothing has changed — the clean is still booked for{' '}
          {formatDay(detail.scheduledDate)}. Ring the office on (435) 238-3286 if it is urgent.
        </p>
        {open.notes ? <p className="mt-2 text-meta text-bark-900">You said: {open.notes}</p> : null}
      </div>
    );
  }

  if (!detail.canRequestChange) return null;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-ink-300 bg-white p-4 shadow-card">
        <p className="min-w-0 text-body text-ink-700">
          Need to change this? Ask us — nothing moves until we confirm it with you.
        </p>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setAsking('reschedule');
            }}
          >
            Change the date
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setAsking('cancellation');
            }}
          >
            Cancel this clean
          </Button>
        </div>
      </div>

      <ChangeRequestDialog
        job={detail}
        kind={asking}
        onClose={() => {
          setAsking(null);
        }}
      />
    </>
  );
}

function Checklist({ detail }: { detail: ClientJobDetail }) {
  if (detail.tasks.length === 0) {
    return (
      <section className="mt-8">
        <h2 className="mb-3 text-h2 text-ink-900">What we cleaned</h2>
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <EmptyState
            title="No checklist on this clean."
            hint="Some visits are one-off jobs without a standard task list. Ring us if you would like one added."
          />
        </div>
      </section>
    );
  }

  return (
    <section className="mt-8">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-h2 text-ink-900">What we cleaned</h2>
        {/* Zero is `0` — P1. "0 of 10 done" is a fact, not an empty state. */}
        <p className="text-meta text-ink-500">
          {detail.taskCounts.completed} of {detail.taskCounts.total} done
        </p>
      </div>

      <ul className="divide-y divide-ink-300 overflow-hidden rounded-lg border border-ink-300 bg-white shadow-card">
        {detail.tasks.map((task) => (
          <li key={task.id} className="flex items-start gap-3 px-4 py-3">
            <span
              aria-hidden="true"
              className={
                task.isCompleted
                  ? 'mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-status-completed/20 text-status-completed-text'
                  : 'mt-0.5 size-5 shrink-0 rounded-full border border-ink-300'
              }
            >
              {task.isCompleted ? <Check aria-hidden="true" className="size-3.5" /> : null}
            </span>

            <span className="min-w-0 flex-1">
              <span className="block text-body text-ink-900">{task.name}</span>
              <span className="sr-only">{task.isCompleted ? 'Done' : 'Not done yet'}</span>
              {/* Per-task photo counts are surfaced to the client in the
                  prototype too — FINDINGS.md §10.3. */}
              {task.photoCount > 0 ? (
                <span className="mt-0.5 flex items-center gap-1 text-meta text-ink-500">
                  <Camera aria-hidden="true" className="size-3.5" />
                  {task.photoCount} photo{task.photoCount === 1 ? '' : 's'}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The gallery.
 *
 * The `url` on each photo is a client-scoped app URL, not a storage key and not
 * a public link — the server proves the job is this client's, then either
 * redirects to a one-hour signed R2 URL or streams the bytes.
 */
function Gallery({ photos }: { photos: ClientJobPhotoView[] }) {
  const [open, setOpen] = useState<ClientJobPhotoView | null>(null);

  return (
    <section className="mt-8">
      <h2 className="mb-3 text-h2 text-ink-900">Photos</h2>

      {photos.length === 0 ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <EmptyState
            icon={Camera}
            title="No photos on this clean yet."
            hint="Your cleaner takes photos as they work, so they appear here during and after the visit."
          />
        </div>
      ) : (
        <>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {photos.map((photo) => (
              <li key={photo.id}>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(photo);
                  }}
                  className="block w-full overflow-hidden rounded-lg border border-ink-300 bg-white text-left shadow-card hover:border-honey-500"
                >
                  <img
                    src={photo.url}
                    alt={photo.caption ?? `${photo.kindLabel} photo by ${photo.uploadedByName}`}
                    loading="lazy"
                    className="aspect-square w-full object-cover"
                  />
                  <span className="block px-2 py-1.5">
                    <span className="block text-meta text-ink-900">{photo.kindLabel}</span>
                    <span className="block truncate text-meta text-ink-500">
                      {photo.uploadedByName}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {open ? (
            <div
              role="dialog"
              aria-modal="true"
              aria-label={open.caption ?? `${open.kindLabel} photo`}
              className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/80 p-4"
              onClick={() => {
                setOpen(null);
              }}
            >
              <img
                src={open.url}
                alt={open.caption ?? `${open.kindLabel} photo by ${open.uploadedByName}`}
                className="max-h-[80vh] max-w-full rounded-lg object-contain"
              />
              <p className="mt-3 text-center text-body text-white">
                {open.caption ? <span className="block">{open.caption}</span> : null}
                <span className="text-meta">
                  {open.kindLabel} · {open.uploadedByName} · {formatInstant(open.uploadedAt)}
                </span>
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => {
                  setOpen(null);
                }}
              >
                Close
              </Button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <h2 className="mb-3 text-h3 text-ink-900">{title}</h2>
      {children}
    </div>
  );
}

function Row({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof Clock;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-ink-500" />
      <div className="min-w-0">
        <dt className="text-meta text-ink-500">{label}</dt>
        <dd className="text-body text-ink-900">{children}</dd>
      </div>
    </div>
  );
}

/** "3 bed · 2.5 bath · 1,800 sq ft", leaving out what nobody recorded. */
function describeProperty(property: {
  bedrooms: number | null;
  bathrooms: number | null;
  squareFeet: number | null;
}): string {
  const parts: string[] = [];
  if (property.bedrooms !== null) parts.push(`${String(property.bedrooms)} bed`);
  if (property.bathrooms !== null) parts.push(`${String(property.bathrooms)} bath`);
  if (property.squareFeet !== null) parts.push(`${property.squareFeet.toLocaleString()} sq ft`);

  return parts.length > 0 ? parts.join(' · ') : 'Size not recorded';
}
